"use client";

import { useMemo, useState, useId } from "react";
import { useLocale, useTranslations } from "next-intl";
import { CheckCircle2 } from "lucide-react";

export function PeptideCalcWidget() {
	const t = useTranslations("home.tools.calc");
	const locale = useLocale();
	const formatNum = (n: number, digits = 2) =>
		n.toLocaleString(locale, {
			minimumFractionDigits: digits,
			maximumFractionDigits: digits,
		});
	const [dose, setDose] = useState(10);
	const [water, setWater] = useState(2);
	const [target, setTarget] = useState(0.25);
	const doseId = useId();
	const waterId = useId();
	const targetId = useId();

	const concentration = dose / water; // mg/ml
	const injectionMl = target / concentration;
	const insulinIu = useMemo(() => Math.round(injectionMl * 100 * 10) / 10, [injectionMl]);

	return (
		<div>
			<div className="mb-4 flex items-center justify-between">
				<div className="text-sm font-bold text-foreground">{t("title")}</div>
				<span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
					<CheckCircle2 className="h-3 w-3" aria-hidden="true" />
					{t("interactive")}
				</span>
			</div>

			<div className="flex flex-col gap-4">
				<CalcRow id={doseId} label={t("vialLabel")} value={`${dose} ${t("mg")}`}>
					<input
						id={doseId}
						type="range"
						min={2}
						max={50}
						step={1}
						value={dose}
						onChange={(e) => setDose(Number(e.target.value))}
						className="w-full accent-[var(--primary-hero)]"
					/>
				</CalcRow>
				<CalcRow id={waterId} label={t("waterLabel")} value={`${water} ${t("ml")}`}>
					<input
						id={waterId}
						type="range"
						min={1}
						max={5}
						step={0.5}
						value={water}
						onChange={(e) => setWater(Number(e.target.value))}
						className="w-full accent-[var(--primary-hero)]"
					/>
				</CalcRow>
				<CalcRow id={targetId} label={t("targetLabel")} value={`${formatNum(target, 2)} ${t("mg")}`}>
					<input
						id={targetId}
						type="range"
						min={0.05}
						max={5}
						step={0.05}
						value={target}
						onChange={(e) => setTarget(Number(e.target.value))}
						className="w-full accent-[var(--primary-hero)]"
					/>
				</CalcRow>
			</div>

			<div className="mt-6 rounded-xl bg-[var(--primary-hero)] p-5 text-white">
				<div className="text-[11px] font-semibold uppercase tracking-widest text-white/70">
					{t("resultEyebrow")}
				</div>
				<div className="mt-2 flex items-baseline justify-between gap-4">
					<span className="text-sm">{t("concentration")}</span>
					<span className="font-mono font-bold">
						{formatNum(concentration, 2)} {t("mgPerMl")}
					</span>
				</div>
				<div className="mt-1 flex items-baseline justify-between gap-4">
					<span className="text-sm">{t("injectionVolume")}</span>
					<span className="font-mono font-bold">
						{formatNum(injectionMl, 3)} {t("ml")}
					</span>
				</div>
				<div className="mt-3 flex items-baseline justify-between gap-4 border-t border-white/15 pt-3">
					<span className="text-sm font-semibold">{t("insulinSyringe")}</span>
					<span className="font-mono text-xl font-extrabold text-emerald-300">
						{formatNum(insulinIu, 1)} {t("iu")}
					</span>
				</div>
			</div>
		</div>
	);
}

interface CalcRowProps {
	id: string;
	label: string;
	value: string;
	children: React.ReactNode;
}

function CalcRow({ id, label, value, children }: CalcRowProps) {
	return (
		<div>
			<div className="mb-1.5 flex items-baseline justify-between">
				<label htmlFor={id} className="text-xs font-medium text-muted-foreground">
					{label}
				</label>
				<span className="font-mono text-sm font-semibold text-[var(--primary-hero)]">{value}</span>
			</div>
			{children}
		</div>
	);
}
